import {
  useGetDataKeyDefinitions,
  usePatchDocumentVersionDataKeysMutation,
} from "@hooks";
import { Document, DocumentVersion } from "@models";
import { DeleteOutline } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
  type GridRowOrderChangeParams,
} from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import GenericModal from "src/components/GenericModal/GenericModal";
import { MoreOptionsButton } from "./DocViewerMoreOptions";

const getOrderedAttachedDataKeyKeys = (selectedVersion: DocumentVersion) =>
  [...selectedVersion.dataKeys]
    .sort((left, right) => {
      if (left.order === null || left.order === undefined) return 1;
      if (right.order === null || right.order === undefined) return -1;
      return left.order - right.order;
    })
    .map((entry) => entry.dataKeyKey);

const arraysEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const DATA_KEYS_MODAL_DESCRIPTION =
  "Add or remove data keys that should be collected in this document. Data keys added here will appear in this document's wizard.";

const DATA_KEYS_MODAL_WARNING =
  "If a data key is already attached to another document, remove it from that document before adding it here. Data key values should only be collected on one document at a time. If you just want to import a value of a data key from another document just put it in {{}} in the doucment content, don't add it here..";

const DATA_KEY_LABEL_MAX_LENGTH = 15;
const DATA_KEY_GRID_HEADER_HEIGHT = 40;
const DATA_KEY_GRID_MAX_HEIGHT = 320;
const DATA_KEY_GRID_MIN_HEIGHT = 128;
const DATA_KEY_GRID_ROW_HEIGHT = 44;

type DataKeyGridRow = {
  __reorder__: string;
  dataKeyKey: string;
  id: string;
  label: string;
  order: number;
};

const truncateDataKeyLabel = (label: string) =>
  label.length > DATA_KEY_LABEL_MAX_LENGTH
    ? `${label.slice(0, DATA_KEY_LABEL_MAX_LENGTH)}…`
    : label;

export const reorderDataKeyKeys = (
  dataKeyKeys: string[],
  oldIndex: number,
  targetIndex: number
) => {
  if (
    oldIndex === targetIndex ||
    oldIndex < 0 ||
    oldIndex >= dataKeyKeys.length ||
    targetIndex < 0 ||
    targetIndex >= dataKeyKeys.length
  ) {
    return dataKeyKeys;
  }

  const reorderedDataKeyKeys = [...dataKeyKeys];
  const [movedDataKeyKey] = reorderedDataKeyKeys.splice(oldIndex, 1);
  reorderedDataKeyKeys.splice(targetIndex, 0, movedDataKeyKey);
  return reorderedDataKeyKeys;
};

export const useDocumentDataKeysAction = ({
  canEditDataKeys,
  deviceId,
  orgId,
  selectedDoc,
  selectedVersion,
}: {
  canEditDataKeys: boolean;
  deviceId: string;
  orgId: string;
  selectedDoc: Document;
  selectedVersion: DocumentVersion;
}) => {
  const patchDataKeysMutation = usePatchDocumentVersionDataKeysMutation();
  const { data: dataKeys = [], isLoading: isLoadingDataKeys } =
    useGetDataKeyDefinitions(canEditDataKeys);
  const [isDataKeysModalOpen, setIsDataKeysModalOpen] = useState(false);
  const [selectedDataKeyKeys, setSelectedDataKeyKeys] = useState<string[]>([]);
  const [initialDataKeyKeys, setInitialDataKeyKeys] = useState<string[]>([]);
  const [pendingDataKeyKey, setPendingDataKeyKey] = useState<string | null>(
    null
  );
  const [dataKeysError, setDataKeysError] = useState<string | null>(null);

  const dataKeyOptions = useMemo(
    () => dataKeys.map((dataKey) => dataKey.key),
    [dataKeys]
  );

  const dataKeyLabelByKey = useMemo(
    () =>
      dataKeys.reduce<Record<string, string | undefined>>((labels, dataKey) => {
        labels[dataKey.key] = dataKey.question.options?.label;
        return labels;
      }, {}),
    [dataKeys]
  );

  const availableDataKeyOptions = useMemo(() => {
    const selectedDataKeyKeySet = new Set(selectedDataKeyKeys);
    return dataKeyOptions.filter(
      (dataKeyKey) => !selectedDataKeyKeySet.has(dataKeyKey)
    );
  }, [dataKeyOptions, selectedDataKeyKeys]);

  const dataKeyRows = useMemo<DataKeyGridRow[]>(
    () =>
      selectedDataKeyKeys.map((dataKeyKey, index) => ({
        __reorder__: dataKeyKey,
        dataKeyKey,
        id: dataKeyKey,
        label: dataKeyLabelByKey[dataKeyKey] ?? "",
        order: index + 1,
      })),
    [dataKeyLabelByKey, selectedDataKeyKeys]
  );

  const handleRemoveDataKey = useCallback((dataKeyKey: string) => {
    setSelectedDataKeyKeys((currentDataKeyKeys) =>
      currentDataKeyKeys.filter(
        (currentDataKeyKey) => currentDataKeyKey !== dataKeyKey
      )
    );
  }, []);

  const dataKeyColumns = useMemo<GridColDef<DataKeyGridRow>[]>(
    () => [
      {
        align: "center",
        field: "order",
        headerAlign: "center",
        headerName: "Order",
        sortable: false,
        width: 72,
      },
      {
        field: "dataKeyKey",
        flex: 1,
        headerName: "Data key",
        minWidth: 180,
        sortable: false,
      },
      {
        field: "label",
        flex: 1,
        headerName: "Wizard label",
        minWidth: 180,
        sortable: false,
      },
      {
        field: "actions",
        getActions: ({ row }) => [
          <GridActionsCellItem
            key={`remove-${row.dataKeyKey}`}
            icon={<DeleteOutline />}
            label={`Remove ${row.dataKeyKey}`}
            onClick={() => handleRemoveDataKey(row.dataKeyKey)}
          />,
        ],
        type: "actions",
        width: 52,
      },
    ],
    [handleRemoveDataKey]
  );

  const hasDataKeyChanges = !arraysEqual(
    selectedDataKeyKeys,
    initialDataKeyKeys
  );
  const isSaveDisabled =
    patchDataKeysMutation.isPending || isLoadingDataKeys || !hasDataKeyChanges;

  const getOptionLabel = (dataKeyKey: string) => {
    const label = dataKeyLabelByKey[dataKeyKey];
    return label
      ? `${dataKeyKey} (${truncateDataKeyLabel(label)})`
      : dataKeyKey;
  };

  const handleAddDataKey = () => {
    if (!pendingDataKeyKey) {
      return;
    }

    setSelectedDataKeyKeys((currentDataKeyKeys) =>
      currentDataKeyKeys.includes(pendingDataKeyKey)
        ? currentDataKeyKeys
        : [...currentDataKeyKeys, pendingDataKeyKey]
    );
    setPendingDataKeyKey(null);
  };

  const handleRowOrderChange = ({
    oldIndex,
    targetIndex,
  }: GridRowOrderChangeParams) => {
    setSelectedDataKeyKeys((currentDataKeyKeys) =>
      reorderDataKeyKeys(currentDataKeyKeys, oldIndex, targetIndex)
    );
  };

  const handleOpenDataKeysModal = () => {
    const attachedDataKeyKeys = getOrderedAttachedDataKeyKeys(selectedVersion);
    setSelectedDataKeyKeys(attachedDataKeyKeys);
    setInitialDataKeyKeys(attachedDataKeyKeys);
    setPendingDataKeyKey(null);
    setDataKeysError(null);
    setIsDataKeysModalOpen(true);
  };

  const handleCloseDataKeysModal = () => {
    setPendingDataKeyKey(null);
    setDataKeysError(null);
    setIsDataKeysModalOpen(false);
  };

  const handleSaveDataKeys = async () => {
    if (isSaveDisabled) {
      return;
    }

    try {
      await patchDataKeysMutation.mutateAsync({
        orgId,
        deviceId,
        docId: selectedDoc.id,
        docVersionId: selectedVersion.id,
        dataKeyKeys: selectedDataKeyKeys,
      });
      setDataKeysError(null);
      setIsDataKeysModalOpen(false);
    } catch {
      setDataKeysError("Failed to update document data keys.");
    }
  };

  const dataKeysButton: MoreOptionsButton | null = canEditDataKeys
    ? {
        title: "Edit wizard data keys",
        onClick: handleOpenDataKeysModal,
        disabled: false,
      }
    : null;

  const dataKeysModal = (
    <GenericModal
      isOpen={isDataKeysModalOpen}
      title="Wizard Data Keys 🧙"
      handleCancel={handleCloseDataKeysModal}
      handleSave={handleSaveDataKeys}
      isLoading={patchDataKeysMutation.isPending}
      isDisabled={isSaveDisabled}
      width="720px"
      description={DATA_KEYS_MODAL_DESCRIPTION}
      alertSeverity="warning"
      alertText={DATA_KEYS_MODAL_WARNING}
    >
      {dataKeysError && <Alert severity="error">{dataKeysError}</Alert>}
      <Box
        sx={{
          alignItems: "flex-start",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Autocomplete
          disabled={isLoadingDataKeys}
          fullWidth
          loading={isLoadingDataKeys}
          noOptionsText="All data keys have been added"
          options={availableDataKeyOptions}
          size="small"
          value={pendingDataKeyKey}
          onChange={(_, dataKeyKey) => setPendingDataKeyKey(dataKeyKey)}
          getOptionLabel={getOptionLabel}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add data key"
              size="small"
              autoComplete="off"
            />
          )}
          ListboxProps={{ style: { maxHeight: "min(260px, 35vh)" } }}
        />
        <Button
          disabled={!pendingDataKeyKey || isLoadingDataKeys}
          onClick={handleAddDataKey}
          variant="contained"
        >
          Add
        </Button>
      </Box>
      <Typography color="text.secondary" variant="caption">
        Drag rows to change the order in the wizard.
      </Typography>
      <Box
        aria-label="Selected wizard data keys"
        data-testid="wizard-data-keys-grid"
        role="region"
        sx={{
          height: Math.min(
            DATA_KEY_GRID_MAX_HEIGHT,
            Math.max(
              DATA_KEY_GRID_MIN_HEIGHT,
              DATA_KEY_GRID_HEADER_HEIGHT +
                dataKeyRows.length * DATA_KEY_GRID_ROW_HEIGHT +
                2
            )
          ),
        }}
      >
        <DataGridPremium
          autoHeight={process.env.NODE_ENV === "test"}
          columnHeaderHeight={DATA_KEY_GRID_HEADER_HEIGHT}
          columns={dataKeyColumns}
          disableColumnFilter
          disableColumnMenu
          disableColumnReorder
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooter
          loading={isLoadingDataKeys}
          localeText={{ noRowsLabel: "No wizard data keys selected" }}
          onRowOrderChange={handleRowOrderChange}
          rowHeight={DATA_KEY_GRID_ROW_HEIGHT}
          rowReordering
          rows={dataKeyRows}
          sx={{
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none",
              },
          }}
        />
      </Box>
    </GenericModal>
  );

  return {
    dataKeysButton,
    dataKeysModal,
  };
};
